(function () {
  const CHECKOUT_SELECTOR = '[name="checkout"], [data-dd-wholesale-checkout], a[href$="/checkout"], a[href*="/checkout?"]';

  function storefrontRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  }

  class AgencyOrder extends HTMLElement {
    constructor() {
      super();
      this.abortController = new AbortController();
      this.saveTimer = 0;
      this.saving = false;
      this.needsSave = false;
      this.lastSavedFingerprint = '';
    }

    connectedCallback() {
      if (this.abortController.signal.aborted) this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.fields.forEach((field) => {
        field.addEventListener('input', this.handleInput, { signal });
        field.addEventListener('change', this.handleChange, { signal });
        field.addEventListener('blur', this.handleBlur, { signal });
      });

      this.lastSavedFingerprint = this.dataset.ddAgencyValid === 'true'
        ? this.fingerprint(this.attributesPayload())
        : '';
      const complete = this.validate(false);
      if (complete && this.dataset.ddAgencyValid !== 'true') this.queueSave(0);
    }

    disconnectedCallback() {
      window.clearTimeout(this.saveTimer);
      this.abortController.abort();
    }

    get fields() {
      return Array.from(this.querySelectorAll('[data-dd-agency-field]'));
    }

    fieldValue(field) {
      return String(field.value || '').trim();
    }

    fieldIsValid(field) {
      const value = this.fieldValue(field);
      if (!value) return false;
      if (field.hasAttribute('data-dd-agency-ci')) {
        const digits = value.replace(/\D/g, '');
        return digits.length >= 7 && digits.length <= 8;
      }
      return true;
    }

    attributesPayload() {
      const attributes = {};
      this.querySelectorAll('[data-dd-attribute-key]').forEach((field) => {
        if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;
        attributes[field.dataset.ddAttributeKey] = this.fieldValue(field);
      });
      return attributes;
    }

    fingerprint(attributes) {
      return JSON.stringify(attributes);
    }

    handleInput = (event) => {
      this.clearFieldError(event.currentTarget);
      this.validate(false);
      this.setSaveState('pending', 'Cambios sin guardar');
      this.queueSave(550);
    };

    handleChange = (event) => {
      this.clearFieldError(event.currentTarget);
      this.validate(false);
      this.queueSave(0);
    };

    handleBlur = () => {
      this.queueSave(0);
    };

    clearFieldError(field) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;
      if (!this.fieldIsValid(field)) return;
      field.removeAttribute('aria-invalid');
      field.closest('.dd-agency-field')?.classList.remove('has-error');
      const error = field.closest('.dd-agency-field')?.querySelector('[data-dd-field-error]');
      if (error instanceof HTMLElement) error.hidden = true;
    }

    validate(revealErrors) {
      let completed = 0;
      let firstInvalid = null;

      this.fields.forEach((field) => {
        if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;
        const valid = this.fieldIsValid(field);
        if (valid) completed += 1;
        if (!valid && !firstInvalid) firstInvalid = field;

        if (revealErrors) {
          if (valid) field.removeAttribute('aria-invalid');
          else field.setAttribute('aria-invalid', 'true');
          field.closest('.dd-agency-field')?.classList.toggle('has-error', !valid);
          const error = field.closest('.dd-agency-field')?.querySelector('[data-dd-field-error]');
          if (error instanceof HTMLElement) error.hidden = valid;
        }
      });

      const valid = completed === this.fields.length && this.fields.length === 6;
      this.dataset.ddAgencyValid = String(valid);
      this.classList.toggle('is-complete', valid);
      this.firstInvalidField = firstInvalid;
      this.updateProgress(completed);
      this.updateCheckout(valid);
      return valid;
    }

    updateProgress(completed) {
      const count = this.querySelector('[data-dd-agency-count]');
      const progress = this.querySelector('[data-dd-agency-progress]');
      const bar = this.querySelector('[data-dd-agency-progress-bar]');
      const total = this.fields.length || 6;
      if (count) count.textContent = completed + '/' + total;
      if (progress) progress.setAttribute('aria-valuenow', String(completed));
      if (bar instanceof HTMLElement) bar.style.setProperty('--dd-agency-progress', completed / total * 100 + '%');
    }

    updateCheckout(valid) {
      const readyLabel = this.dataset.ddCheckoutReadyLabel || 'Pagar pedido';
      const incompleteLabel = this.dataset.ddCheckoutIncompleteLabel || 'Completá los datos de agencia';

      document.querySelectorAll('[data-dd-wholesale-checkout]').forEach((button) => {
        if (!(button instanceof HTMLElement) || button.dataset.ddWholesaleValid !== 'true') return;
        button.dataset.ddWholesaleAgencyValid = String(valid);
        button.classList.toggle('dd-checkout-needs-agency', !valid);
        const buttonText = button.querySelector('.button-text');
        if (buttonText) buttonText.textContent = valid ? readyLabel : incompleteLabel;
        if (valid) {
          if (button.getAttribute('aria-describedby') === this.id) button.removeAttribute('aria-describedby');
        } else {
          button.setAttribute('aria-describedby', this.id);
        }
      });
    }

    queueSave(delay) {
      window.clearTimeout(this.saveTimer);
      this.saveTimer = window.setTimeout(() => this.persist(), delay);
    }

    async persist() {
      const attributes = this.attributesPayload();
      const fingerprint = this.fingerprint(attributes);
      if (fingerprint === this.lastSavedFingerprint) {
        this.setSaveState('saved', this.validate(false) ? 'Datos guardados' : 'Datos guardados hasta ahora');
        return;
      }

      if (this.saving) {
        this.needsSave = true;
        return;
      }

      this.saving = true;
      this.needsSave = false;
      this.setSaveState('saving', 'Guardando…');

      try {
        const response = await fetch(storefrontRoot() + 'cart/update.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes }),
        });
        if (!response.ok) throw new Error('No se pudieron guardar los datos.');
        await response.json();
        this.lastSavedFingerprint = fingerprint;
        const complete = this.validate(false);
        this.setSaveState('saved', complete ? 'Datos guardados' : 'Datos guardados hasta ahora');
        const live = this.querySelector('[data-dd-agency-live]');
        if (live) live.textContent = 'Datos de agencia guardados.';
      } catch (error) {
        this.setSaveState('error', 'No se pudo guardar. Reintentando…');
        this.needsSave = true;
      } finally {
        this.saving = false;
        const currentFingerprint = this.fingerprint(this.attributesPayload());
        if (this.needsSave || currentFingerprint !== this.lastSavedFingerprint) this.queueSave(900);
      }
    }

    setSaveState(state, label) {
      const stateElement = this.querySelector('[data-dd-agency-save-state]');
      const labelElement = this.querySelector('[data-dd-agency-save-label]');
      if (stateElement instanceof HTMLElement) stateElement.dataset.state = state;
      if (labelElement) labelElement.textContent = label;
    }

    revealAndFocus() {
      this.validate(true);
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => this.firstInvalidField?.focus({ preventScroll: true }), 380);
      const live = this.querySelector('[data-dd-agency-live]');
      if (live) live.textContent = 'Completá los datos obligatorios de agencia antes de pagar.';
      this.classList.remove('needs-attention');
      window.requestAnimationFrame(() => {
        this.classList.add('needs-attention');
        window.setTimeout(() => this.classList.remove('needs-attention'), 900);
      });
    }
  }

  function checkoutIntent(event) {
    if (event.type === 'submit') {
      return event.target instanceof HTMLFormElement && event.target.id === 'cart-form';
    }
    return event.target instanceof Element && Boolean(event.target.closest(CHECKOUT_SELECTOR));
  }

  function guardAgencyCheckout(event) {
    if (!checkoutIntent(event)) return;
    const guide = document.querySelector('dd-agency-order');
    if (!(guide instanceof AgencyOrder) || guide.validate(false)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    guide.revealAndFocus();
  }

  if (!customElements.get('dd-agency-order')) {
    customElements.define('dd-agency-order', AgencyOrder);
  }

  document.addEventListener('click', guardAgencyCheckout, true);
  document.addEventListener('submit', guardAgencyCheckout, true);
})();
